var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { styled } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import merge from 'lodash/merge';
import React from 'react';
import { BulkActionsToolbar, BulkDeleteButton, RecordContextProvider, } from 'react-admin';
import { useDatagridAGController } from './useDatagridAGController';
var defaultBulkActionButtons = React.createElement(BulkDeleteButton, null);
var raDefaultColDef = {
    resizable: true,
    filter: true,
    sortable: true,
    editable: true,
    headerCheckboxSelectionFilteredOnly: true,
    headerCheckboxSelectionCurrentPageOnly: true,
};
// Augment colDefs to also support the case when cellRenderer is a React element
var augmentColDefs = function (colDefs) {
    if (!colDefs) {
        return [];
    }
    return colDefs.map(function (colDef) {
        var cellRenderer = colDef.cellRenderer;
        if (cellRenderer != null && React.isValidElement(cellRenderer)) {
            return __assign(__assign({}, colDef), { cellRenderer: getRaCellRenderer(cellRenderer) });
        }
        else {
            return colDef;
        }
    });
};
var getRaCellRenderer = function (cellRenderer) {
    // eslint-disable-next-line react/display-name
    return function (_a) {
        var data = _a.data;
        return (React.createElement(RecordContextProvider, { value: data }, cellRenderer));
    };
};
/**
 * `<DatagridAG>` is an alternative datagrid component with advanced features, based on [ag-grid](https://www.ag-grid.com/).
 *
 * Use `<DatagridAG>` as a child of a react-admin `<List>`, `<ReferenceManyField>`, or any other component that creates a `ListContext`.
 *
 * @example
 * ```tsx
 * import 'ag-grid-community/styles/ag-grid.css';
 * import 'ag-grid-community/styles/ag-theme-alpine.css';
 * import React from 'react';
 * import { List } from 'react-admin';
 * import { DatagridAG } from '@react-admin/ra-datagrid-ag';
 *
 * export const PostList = () => {
 *     const columnDefs = [
 *         { field: 'title' },
 *         { field: 'published_at' },
 *         { field: 'body' },
 *     ];
 *     return (
 *         <List perPage={10000} pagination={false}>
 *             <DatagridAG columnDefs={columnDefs} />
 *         </List>
 *     );
 * };
 * ```
 */
export var DatagridAG = React.forwardRef(function (props, outerRef) {
    var _a = props.bulkActionButtons, bulkActionButtons = _a === void 0 ? defaultBulkActionButtons : _a, className = props.className, columnDefsProp = props.columnDefs, defaultColDefProp = props.defaultColDef, _mutationOptionsProp = props.mutationOptions, sx = props.sx, _b = props.theme, theme = _b === void 0 ? 'ag-theme-alpine' : _b, rest = __rest(props, ["bulkActionButtons", "className", "columnDefs", "defaultColDef", "mutationOptions", "sx", "theme"]);
    // We need to both expose the ref to the parent component (so they can use the api)
    // and use it internally (so we can call api methods too).
    // That's why we need useImperativeHandle here.
    var innerRef = React.useRef(null);
    React.useImperativeHandle(outerRef, function () { return innerRef.current; }, []);
    var defaultColDef = React.useMemo(function () { return merge({}, raDefaultColDef, defaultColDefProp); }, [defaultColDefProp]);
    var columnDefs = React.useMemo(function () { return augmentColDefs(columnDefsProp); }, [columnDefsProp]);
    var _c = useDatagridAGController(__assign(__assign({}, props), { gridRef: innerRef })), rowData = _c.rowData, selectedIds = _c.selectedIds, controller = __rest(_c, ["rowData", "selectedIds"]);
    return (React.createElement(Root, { sx: sx, className: clsx(DatagridAGClasses.root, className, theme) },
        bulkActionButtons !== false ? (React.createElement(BulkActionsToolbar, { selectedIds: selectedIds }, React.isValidElement(bulkActionButtons)
            ? bulkActionButtons
            : defaultBulkActionButtons)) : null,
        React.createElement(AgGridReact, __assign({ ref: innerRef, rowData: rowData, columnDefs: columnDefs, defaultColDef: defaultColDef, pagination: true, paginationAutoPageSize: true, animateRows: true, rowSelection: "multiple", suppressRowClickSelection: true, readOnlyEdit: true, getRowId: function (params) { return String(params.data.id); } }, rest, controller))));
});
DatagridAG.displayName = 'DatagridAG';
var PREFIX = 'RaDatagridAG';
export var DatagridAGClasses = {
    root: "".concat(PREFIX, "-root"),
};
var Root = styled('div', {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function (_a) {
    var theme = _a.theme;
    return ({
        height: "calc(100vh - 48px - 48px - ".concat(theme.spacing(1), ")"),
    });
});
